import { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import useEscapeKey from '../../hooks/useEscapeKey';
import useClubVocab from '../../hooks/useClubVocab';
import { useConfirm } from '../common/ConfirmModal';
import { useCalendar } from '../../context/CalendarContext';
import DualDatePicker from '../common/DualDatePicker';
import Button from '../common/Button';
import {
  MdReceipt, MdClose, MdUpload, MdCheck, MdTune,
} from 'react-icons/md';
import * as feeSvc from '../../services/feeService';
import { isMethodEnabled } from '../../utils/paymentMethods';
import { adHint } from '../../utils/calendar';

// Kathmandu-local (UTC+5:45) current month "YYYY-MM". Shifting the epoch by the offset
// and reading UTC parts avoids the toISOString() UTC-boundary off-by-one at month end.
const kthNow     = new Date(Date.now() + (5 * 60 + 45) * 60 * 1000);
const THIS_MONTH = `${kthNow.getUTCFullYear()}-${String(kthNow.getUTCMonth() + 1).padStart(2, '0')}`;

const LABEL_CLS = 'block text-xs font-medium text-muted mb-1.5';
const INPUT_CLS = 'w-full bg-card border border-divider rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-secondary/30';

// Shared "Record Payment" modal — extracted from Payments.jsx so both the Payments page
// (Collect tab) and Receivables can open the exact same flow in place, with no page
// navigation. Behavior is unchanged from its original Payments.jsx location.
//
// defaultPayment is used three ways: EDITING an existing MONTHLY record (full doc, has _id),
// PRESELECTING a student ({ studentId }, e.g. from Collect or Receivables), or opening from a
// specific CUSTOM CHARGE row (type === 'custom' → that charge is pre-selected below). Only a
// real monthly edit should suppress the Base Fee prefill — a custom-charge open must still let
// the base fee follow the selected months.
export default function RecordPaymentModal({ onClose, onSuccess, defaultPayment }) {
  useEscapeKey(onClose);
  const v = useClubVocab();
  const [confirm, confirmModal] = useConfirm();
  const { formatMonth } = useCalendar();
  const openedFromCharge = defaultPayment?.type === 'custom';
  const isEditing = !!defaultPayment?._id && !openedFromCharge;
  const [studentSearch,   setStudentSearch]   = useState(defaultPayment?.studentId?.name || '');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showList,        setShowList]        = useState(false);
  const [month,     setMonth]     = useState(defaultPayment?.month || THIS_MONTH);
  // True once the admin picks a month manually (picker or chip) — stops the unpaid-months
  // fetch from stomping their choice. Editing an existing record counts as already-set.
  const [monthTouched, setMonthTouched] = useState(!!defaultPayment?.month);
  const [method,    setMethod]    = useState(defaultPayment?.method || 'cash');
  const [discount,  setDiscount]  = useState(String(defaultPayment?.discount || '0'));
  const [paidDate,  setPaidDate]  = useState(new Date().toISOString().slice(0, 10));
  const [bankName,  setBankName]  = useState('');
  const [txRef,     setTxRef]     = useState('');
  const [notes,     setNotes]     = useState('');
  // Multi-month: months chosen via unpaid chips. When ≥1 is selected, chips (not the
  // picker) are the source of truth and the payment is recorded once per month.
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [submitting,     setSubmitting]     = useState(false);
  // Editable "amount received" for the SINGLE-fee case (partial payments). Defaults to the
  // fee's full outstanding balance; the admin can lower it to record a part-payment.
  const [amountInput,   setAmountInput]   = useState('');
  const [amountTouched, setAmountTouched] = useState(false);
  // Base fee comes from the fee doc / structure and the late fee from what the cron stamped —
  // neither is user-typed. Only the DISCOUNT is adjustable, behind an "Adjust amounts" toggle
  // (collapsed every open). manualDiscount flips true once the admin edits it. Editing an
  // existing record starts in manual mode so its stored discount is kept.
  const [adjustOpen,     setAdjustOpen]     = useState(false);
  const [manualDiscount, setManualDiscount] = useState(isEditing);
  // Custom charges are multi-selectable toggles, exactly like month chips — they add ON TOP of
  // any selected months rather than switching the modal into an exclusive mode. Opening from a
  // specific custom charge row simply pre-selects that one charge; the admin can still add
  // months and other charges. Selection holds full charge objects (for the total + breakdown).
  const [selectedCharges, setSelectedCharges] = useState(openedFromCharge ? [defaultPayment] : []);
  const isChargeSelected = (c) => selectedCharges.some(x => String(x._id) === String(c._id));
  const toggleCharge = (c) => {
    setSelectedCharges(prev => prev.some(x => String(x._id) === String(c._id))
      ? prev.filter(x => String(x._id) !== String(c._id))
      : [...prev, c]);
  };
  // Optional payment proof (image/PDF) — one file covers all months in a multi-month record.
  const [proofFile,    setProofFile]    = useState(null);
  const [proofPreview, setProofPreview] = useState(null); // object URL for image thumbnails
  const inputRef = useRef(null);
  const qc = useQueryClient();

  const onProofChange = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'].includes(f.type)) {
      toast.error('Only JPEG, PNG, WEBP, or PDF files are allowed'); return;
    }
    if (f.size > 5 * 1024 * 1024) { toast.error('File must be 5 MB or smaller'); return; }
    if (proofPreview) URL.revokeObjectURL(proofPreview);
    setProofFile(f);
    setProofPreview(f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
  };
  const clearProof = () => {
    if (proofPreview) URL.revokeObjectURL(proofPreview);
    setProofFile(null);
    setProofPreview(null);
  };
  // Release the object URL on unmount.
  useEffect(() => () => { if (proofPreview) URL.revokeObjectURL(proofPreview); }, [proofPreview]);

  const { data: studentsData } = useQuery({
    queryKey: ['fee-students'],
    queryFn:  () => feeSvc.getStudentsForPayment().then(r => r.data),
  });
  const { data: structureData } = useQuery({
    queryKey: ['fee-structure'],
    queryFn:  () => feeSvc.getFeeStructure().then(r => r.data),
  });
  // Which payment methods the club has enabled — filters the method buttons below.
  const { data: paySettingsData } = useQuery({
    queryKey: ['payment-settings'],
    queryFn:  () => feeSvc.getPaymentSettings().then(r => r.data),
  });
  const acceptedMethods = paySettingsData?.paymentSettings?.acceptedPaymentMethods || [];

  // If the current method got filtered out (not enabled), fall back to the first enabled one.
  useEffect(() => {
    if (acceptedMethods.length === 0) return;
    if (!isMethodEnabled(method, acceptedMethods)) {
      const first = ['cash', 'bank', 'esewa', 'khalti', 'fonepay', 'other'].find(m => isMethodEnabled(m, acceptedMethods));
      if (first && first !== method) setMethod(first);
    }
  }, [acceptedMethods, method]);

  // Unpaid (pending + overdue) fees for the selected student — powers the strip + oldest-month default.
  const { data: unpaidData, isFetching: unpaidLoading } = useQuery({
    queryKey: ['student-unpaid-months', selectedStudent?._id],
    queryFn:  () => feeSvc.getAllPayments({ studentId: selectedStudent._id, status: 'pending,overdue,partial', limit: 100 }).then(r => r.data),
    enabled:  !!selectedStudent?._id,
  });

  // Dedupe by month (combine amounts if two records share one), then sort ascending by
  // 'YYYY-MM' string — plain lexicographic, no Date parsing / UTC math.
  const unpaidRaw    = unpaidData?.payments || unpaidData?.data || [];
  // One-off custom charges are paid by _id, not by month — pull them out so they don't get
  // silently merged into a month total, and surface them as their own selectable chips.
  const customUnpaid = unpaidRaw.filter(p => (p.type || 'monthly') === 'custom');
  const unpaidByMonth = new Map();   // month -> owed total (base + stamped late - discount), for the strip
  const monthMeta     = new Map();   // month -> { base, lateFee, discount } straight from the fee doc(s)
  for (const p of unpaidRaw) {
    if ((p.type || 'monthly') === 'custom') continue;
    // OWED = remaining balance (partial-aware; balance comes from getAllPayments/deriveAmounts).
    // Falls back to totalAmount for any row without a balance field.
    const owed = p.balance != null ? p.balance : (p.totalAmount || p.amount || 0);
    unpaidByMonth.set(p.month, (unpaidByMonth.get(p.month) || 0) + owed);
    const prev = monthMeta.get(p.month) || { base: 0, lateFee: 0, discount: 0 };
    monthMeta.set(p.month, {
      base:     prev.base     + (p.amount   || 0),
      lateFee:  prev.lateFee  + (p.lateFee  || 0),
      discount: prev.discount + (p.discount || 0),
    });
  }
  const unpaidMonths = [...unpaidByMonth.entries()]
    .map(([m, amount]) => ({ month: m, amount }))
    .sort((a, b) => (a.month < b.month ? -1 : a.month > b.month ? 1 : 0));
  const oldestUnpaid = unpaidMonths[0]?.month || null;
  const unpaidTotal  = unpaidMonths.reduce((s, m) => s + m.amount, 0);

  // Default the picker to the oldest unpaid month once fetched — unless the admin already
  // chose one (manual pick or editing an existing record).
  useEffect(() => {
    if (oldestUnpaid && !monthTouched) setMonth(oldestUnpaid);
  }, [oldestUnpaid, monthTouched]);

  const students = studentsData?.students || [];
  const filteredStudents = students.filter(s =>
    s.name.toLowerCase().includes(studentSearch.toLowerCase()) ||
    (s.studentProfile?.studentId || '').toLowerCase().includes(studentSearch.toLowerCase())
  );

  // Pre-select student if editing existing payment
  useEffect(() => {
    if (defaultPayment && students.length) {
      const uid = defaultPayment.studentId?._id || defaultPayment.studentId;
      const found = students.find(s => s._id === uid || s._id === uid?.toString());
      if (found) {
        setSelectedStudent(found);
        setStudentSearch(found.name);
      }
    }
  }, [students, defaultPayment]);

  // Selected custom charges (fixed amounts — no late fee / discount applied to them).
  const chargeCount = selectedCharges.length;
  const chargeIds   = selectedCharges.map(c => String(c._id));
  const chargeTotal = selectedCharges.reduce((s, c) => s + Number(c.totalAmount || c.amount || 0), 0);

  // Chip mode (≥1 month chip) → chips drive the months; else the single picker month. The
  // single picker only contributes when NO charges are selected — so a charge-only payment
  // (charges but no month chip) doesn't silently pull in the current month. This leaves the
  // pure-monthly flow (no charges selected) behaving exactly as before.
  const chipMode    = selectedMonths.length >= 1;
  const monthsToPay = chipMode
    ? [...selectedMonths].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)) // oldest first
    : (chargeCount === 0 && month ? [month] : []);
  const monthCount  = monthsToPay.length;
  const multi       = monthCount > 1;

  // Base fee from the club fee structure (student override wins). Used for a month with no fee
  // doc yet, and as the "Adjust amounts" prefill.
  const structureBase = Number(selectedStudent?.studentProfile?.monthlyFee || structureData?.structure?.monthlyFee || 0);

  // Each month's real values, straight from its fee doc (no frontend recomputation of late
  // fees). A month with no doc yet falls back to the structure base with no late/discount.
  const monthBaseOf = (mo) => { const m = monthMeta.get(mo); return m ? m.base : structureBase; };
  const monthLateOf = (mo) => monthMeta.get(mo)?.lateFee  || 0;
  const monthDiscOf = (mo) => monthMeta.get(mo)?.discount || 0;

  // Effective per-month amounts. Base + late fee are ALWAYS sourced from the fee doc (never
  // typed). Discount is auto from the doc unless the admin overrode it via "Adjust amounts"
  // (single-month only, matching the prior behaviour).
  const effBase = (mo) => monthBaseOf(mo);   // always the fee doc / structure base — never typed
  const effLate = (mo) => monthLateOf(mo);   // always what the overdue cron stamped — never typed
  const effDisc = (mo) => manualDiscount ? (multi ? 0 : (parseFloat(discount) || 0)) : monthDiscOf(mo);

  // ── Per-month owed / paid (partial-aware) ─────────────────────────────────────────
  // OWED = remaining BALANCE, never the full charge — a partially-paid month contributes only
  // what's still due (matching the strip). Falls back to the full charge for a month that has no
  // fee doc yet (an advance month being paid ahead).
  const rowBalanceOf  = (r) => (r && r.balance != null ? r.balance : ((r?.totalAmount ?? r?.amount) || 0));
  const rowPaidOf     = (r) => (r && r.amountPaid != null ? r.amountPaid : 0);
  const monthRowOf    = (mo) => unpaidRaw.find(p => p.month === mo && (p.type || 'monthly') !== 'custom') || null;
  const monthOwedOf   = (mo) => { const r = monthRowOf(mo); return r ? rowBalanceOf(r) : (effBase(mo) + effLate(mo) - effDisc(mo)); };
  const monthPaidOf   = (mo) => rowPaidOf(monthRowOf(mo));
  const monthChargeOf = (mo) => { const r = monthRowOf(mo); return r ? ((r.totalAmount ?? r.amount) || 0) : (effBase(mo) + effLate(mo) - effDisc(mo)); };

  // BALANCE-based total — the sum of what's still owed on each selected month (+ charges).
  const monthsTotal = monthsToPay.reduce((s, mo) => s + monthOwedOf(mo), 0);
  const totalAmount = monthsTotal + chargeTotal;

  // ── Single-fee partial support ──────────────────────────────────────────────────
  // Partial editing is only possible on ONE EXISTING fee row: the combined/multi path and
  // not-yet-generated months can't take a partial amount (Stage 1). Everything else keeps the
  // current full-payment behaviour byte-for-byte.
  const singleSelected = (monthCount + chargeCount) === 1;
  const singleMonth    = singleSelected && chargeCount === 0 ? monthsToPay[0] : null;
  const singleMonthRow = singleMonth ? monthRowOf(singleMonth) : null;
  const singleCharge   = singleSelected && chargeCount === 1 ? selectedCharges[0] : null;
  const singleFeeRow   = singleMonthRow || singleCharge || null; // an EXISTING fee doc
  const partialCapable = !!singleFeeRow;
  const outstanding    = partialCapable ? rowBalanceOf(singleFeeRow) : totalAmount;

  // Reset the amount to the outstanding default whenever the single selection changes.
  const singleFeeKey = partialCapable ? String(singleFeeRow._id || singleMonth || '') : '';
  useEffect(() => { setAmountTouched(false); }, [singleFeeKey]);
  useEffect(() => {
    if (!amountTouched) setAmountInput(partialCapable ? String(outstanding) : '');
  }, [partialCapable, outstanding, amountTouched]);

  const parsedAmount    = Number(String(amountInput).replace(/,/g, ''));
  // An amount above the outstanding balance is no longer blocked — it's likely paying this
  // fee in full plus the next one in advance (e.g. a prorated first month). The backend offers
  // a preview ("NPR X settles Ashwin. NPR Y will be applied to Kartik.") via a 409 that this
  // modal catches on submit and confirms through — see handleSubmit below.
  const amountOver      = partialCapable && Number.isFinite(parsedAmount) && parsedAmount > outstanding;
  const amountValid     = !partialCapable || (Number.isFinite(parsedAmount) && parsedAmount > 0);
  const remainingAfter  = partialCapable ? Math.max(0, outstanding - parsedAmount) : 0;
  // Use the partial path when paying LESS than the balance, MORE than the balance (forward
  // settlement), or when the row already has part-payments (so the final payment appends +
  // completes rather than overwriting history).
  const isPartialSubmit = partialCapable && amountValid
    && (parsedAmount !== outstanding || rowPaidOf(singleFeeRow) > 0);
  const effectiveAmount = partialCapable ? (Number.isFinite(parsedAmount) ? parsedAmount : 0) : totalAmount;

  // Proof is required for non-cash (bank/digital) methods; optional for cash.
  const proofRequired = method !== 'cash';
  const proofMissing  = proofRequired && !proofFile;
  // Every paid month needs a positive base; charges carry their own fixed amount.
  const monthsValid = monthCount === 0 || monthsToPay.every(mo => effBase(mo) > 0);
  const canSubmit   = (monthCount + chargeCount) >= 1 && monthsValid && !proofMissing
    && (monthCount === 0 || !!selectedStudent) && amountValid;

  const toggleMonth = (mo) => {
    setSelectedMonths(prev => prev.includes(mo) ? prev.filter(x => x !== mo) : [...prev, mo]);
    setMonthTouched(true);
  };

  // ── Discount prefill ────────────────────────────────────────────────────────────
  // The Discount input (hidden until "Adjust amounts") mirrors the fee doc's pre-assigned
  // discount, so opening "Adjust" shows the real figure. Once the admin edits, manualDiscount
  // pins their value and the mirror stops. Reset to auto whenever the student changes.
  const selDiscs        = monthsToPay.map(monthDiscOf);
  const uniformDiscount = monthCount > 0 && selDiscs.every(v => v === selDiscs[0]) ? selDiscs[0] : 0;

  const prefillStudentRef = useRef('');
  useEffect(() => {
    const sid = selectedStudent?._id ? String(selectedStudent._id) : '';
    if (sid !== prefillStudentRef.current) { prefillStudentRef.current = sid; setManualDiscount(isEditing); }
  }, [selectedStudent, isEditing]);

  useEffect(() => {
    if (isEditing || manualDiscount) return; // editing a record / admin took over → keep the value
    setDiscount(String(uniformDiscount || 0));
  }, [uniformDiscount, manualDiscount, isEditing]);

  // Record sequentially, oldest first. Stop on the first failure and report exactly which
  // months went through and which did not — never silently continue. Single-month path is
  // unchanged (one call, server's success message, close on success).
  // Base JSON payload for one month (proof handled separately below).
  // A month that ALREADY has part-payments is settled via the Stage 1 partial path (append its
  // remaining BALANCE to payments[]), never the full path — which would overwrite the charge and
  // fail to complete it. A fresh month keeps the exact full-payment payload as before.
  const payloadFor = (mo) => {
    const common = {
      studentId:            selectedStudent._id,
      month:                mo,
      method,
      paidDate,
      bankName:             method === 'bank' ? (bankName || undefined) : undefined,
      transactionReference: ['bank', 'esewa', 'khalti', 'fonepay'].includes(method) ? (txRef || undefined) : undefined,
      notes:                notes || undefined,
    };
    if (monthPaidOf(mo) > 0) {
      return { ...common, amount: monthOwedOf(mo), isPartial: true };
    }
    return { ...common, amount: effBase(mo), lateFee: effLate(mo), discount: effDisc(mo) };
  };

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;

    // ── Single existing fee — PARTIAL (or partial-completing) payment ───────────────
    // One existing fee row, and either paying less than its balance or topping up a fee that
    // already has part-payments. Sends the Stage 1 `isPartial` flag; the backend appends to
    // payments[] and completes the fee when the balance reaches zero.
    if (isPartialSubmit) {
      setSubmitting(true);
      const scalar = {
        studentId:            selectedStudent?._id,
        amount:               parsedAmount,
        isPartial:            true,
        method,
        paidDate,
        bankName:             method === 'bank' ? (bankName || undefined) : undefined,
        transactionReference: ['bank', 'esewa', 'khalti', 'fonepay'].includes(method) ? (txRef || undefined) : undefined,
        notes:                notes || undefined,
      };
      if (singleCharge) scalar.paymentId = singleCharge._id; // a specific custom charge
      else              scalar.month     = singleMonth;      // the monthly fee for this month

      const send = async (payload) => {
        if (proofFile) {
          const fd = new FormData();
          Object.entries(payload).forEach(([k, val]) => { if (val !== undefined) fd.append(k, val); });
          fd.append('proofFile', proofFile);
          return feeSvc.recordPayment(fd);
        }
        return feeSvc.recordPayment(payload);
      };

      try {
        let r;
        try {
          r = await send(scalar);
        } catch (e) {
          // The amount exceeds this fee's balance — not an overpayment, the excess settles the
          // next month(s) forward. Show the server's own preview and let the admin confirm it
          // before anything is actually applied.
          if (e.response?.status === 409 && e.response?.data?.code === 'PAYMENT_EXCEEDS_BALANCE') {
            const ok = await confirm({
              title: 'Apply the extra to next month?',
              message: e.response.data.message,
              confirmLabel: 'Confirm',
              destructive: false,
            });
            if (!ok) { setSubmitting(false); return; }
            r = await send({ ...scalar, confirmForwardSettlement: true });
          } else {
            throw e;
          }
        }
        setSubmitting(false);
        toast.success(r?.data?.message || 'Payment recorded');
        onSuccess();
      } catch (e) {
        setSubmitting(false);
        toast.error(e.response?.data?.message || 'Failed to record payment');
      }
      return;
    }

    // ── Combined path: one or more custom charges selected (± months) ───────────────
    // Sent as a SINGLE transactional request so months + charges commit together or not at all.
    // GUARD: the combined backend path (recordCombinedPayment → markFeeCompleted) overwrites the
    // charge and marks the month completed WITHOUT appending to payments[] — it cannot settle a
    // month that already has part-payments. Block that mix rather than silently mis-recording;
    // the admin records the partial month on its own (single-fee path handles it correctly).
    if (chargeCount > 0 && monthsToPay.some(mo => monthPaidOf(mo) > 0)) {
      toast.error('A selected month has a part-payment — record it on its own, not together with charges.');
      return;
    }
    if (chargeCount > 0) {
      setSubmitting(true);
      const months = monthsToPay.map(mo => ({
        month:    mo,
        amount:   effBase(mo),
        lateFee:  effLate(mo),
        discount: effDisc(mo),
      }));
      const scalar = {
        studentId:            selectedStudent?._id || undefined,
        method,
        paidDate,
        bankName:             method === 'bank' ? (bankName || undefined) : undefined,
        transactionReference: ['bank', 'esewa', 'khalti', 'fonepay'].includes(method) ? (txRef || undefined) : undefined,
        notes:                notes || undefined,
      };
      try {
        let r;
        if (proofFile) {
          const fd = new FormData();
          Object.entries(scalar).forEach(([k, v]) => { if (v !== undefined) fd.append(k, v); });
          fd.append('months', JSON.stringify(months));
          fd.append('paymentIds', JSON.stringify(chargeIds));
          fd.append('proofFile', proofFile);
          r = await feeSvc.recordPayment(fd);
        } else {
          r = await feeSvc.recordPayment({ ...scalar, months, paymentIds: chargeIds });
        }
        setSubmitting(false);
        toast.success(r?.data?.message || 'Payment recorded');
        onSuccess();
      } catch (e) {
        setSubmitting(false);
        toast.error(e.response?.data?.message || 'Failed to record payment');
      }
      return;
    }

    setSubmitting(true);
    const succeeded = [];
    let failed = null, lastMsg = '', proofUrlForRest = null;
    for (let idx = 0; idx < monthsToPay.length; idx++) {
      const mo   = monthsToPay[idx];
      const base = payloadFor(mo);
      try {
        let r;
        if (proofFile && idx === 0) {
          // First month carries the actual file (multipart). Its URL is reused for the rest.
          const fd = new FormData();
          Object.entries(base).forEach(([k, v]) => { if (v !== undefined) fd.append(k, v); });
          fd.append('proofFile', proofFile);
          r = await feeSvc.recordPayment(fd);
          proofUrlForRest = r?.data?.proofUrl || r?.data?.payment?.transferScreenshot || null;
        } else {
          // No file, or a later month reusing the uploaded proof URL (as a plain string).
          r = await feeSvc.recordPayment({ ...base, transferScreenshot: proofUrlForRest || undefined });
        }
        lastMsg = r?.data?.message || lastMsg;
        succeeded.push(mo);
      } catch (e) {
        failed = { month: mo, message: e.response?.data?.message || 'Failed to record payment' };
        break;
      }
    }
    setSubmitting(false);

    if (!failed) {
      toast.success(monthsToPay.length > 1 ? `Recorded ${monthsToPay.length} payments` : (lastMsg || 'Payment recorded'));
      onSuccess();
      return;
    }

    // Partial or total failure — keep the modal open, refresh so paid months drop off.
    if (succeeded.length) {
      qc.invalidateQueries({ queryKey: ['student-unpaid-months', selectedStudent._id] });
      qc.invalidateQueries({ queryKey: ['fee-payments'] });
      qc.invalidateQueries({ queryKey: ['fee-summary'] });
      qc.invalidateQueries({ queryKey: ['receivables'] });
      setSelectedMonths(prev => prev.filter(m => !succeeded.includes(m)));
      const okLabels = succeeded.map(formatMonth).join(', ');
      const remaining = monthsToPay.length - succeeded.length;
      toast.error(
        `Recorded: ${okLabels}. Failed on ${formatMonth(failed.month)}: ${failed.message}. ${remaining} month${remaining !== 1 ? 's' : ''} not recorded.`,
        { duration: 9000 }
      );
    } else {
      toast.error(failed.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-divider bg-card rounded-t-2xl flex-shrink-0">
          <h2 className="font-bold text-secondary text-base flex items-center gap-2">
            <MdReceipt className="text-secondary" /> Record Payment
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-subtle hover:bg-nav-hover">
            <MdClose className="text-xl" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4 flex-1 overflow-y-auto overscroll-contain scroll-smooth">
          {/* Student */}
          <div>
            <label className={LABEL_CLS}>{v.member} *</label>
            {selectedStudent ? (
              <div className="flex items-center justify-between border border-divider rounded-xl px-3 py-2.5 bg-nav-hover">
                <div>
                  <p className="text-sm font-medium text-secondary">{selectedStudent.name}</p>
                  <p className="text-xs text-subtle">{selectedStudent.studentProfile?.studentId || selectedStudent.email}</p>
                </div>
                <button
                  onClick={() => { setSelectedStudent(null); setStudentSearch(''); setDiscount('0'); setManualDiscount(false); setSelectedMonths([]); setSelectedCharges([]); }}
                  className="text-subtle hover:text-muted ml-2"
                >
                  <MdClose />
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  ref={inputRef}
                  value={studentSearch}
                  onChange={e => { setStudentSearch(e.target.value); setShowList(true); }}
                  onFocus={() => setShowList(true)}
                  onBlur={() => setTimeout(() => setShowList(false), 150)}
                  placeholder="Search by name or member ID…"
                  className={INPUT_CLS}
                />
                {showList && studentSearch && (
                  <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-card border border-divider rounded-xl shadow-lg max-h-52 overflow-y-auto">
                    {filteredStudents.length === 0 ? (
                      <p className="text-sm text-subtle px-3 py-3 text-center">No {v.members.toLowerCase()} found</p>
                    ) : filteredStudents.slice(0, 25).map(s => (
                      <button
                        key={s._id}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => { setSelectedStudent(s); setStudentSearch(s.name); setShowList(false); }}
                        className="w-full text-left px-3 py-2.5 hover:bg-nav-hover flex items-center justify-between border-b border-divider last:border-0"
                      >
                        <div>
                          <p className="text-sm font-medium text-secondary">{s.name}</p>
                          <p className="text-xs text-subtle">{s.studentProfile?.studentId || s.email}</p>
                        </div>
                        {s.studentProfile?.currentGrade && (
                          <span className="text-xs text-subtle ml-2 flex-shrink-0">{s.studentProfile.currentGrade}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Unpaid months strip — appears once a student is selected */}
          {selectedStudent && (
            <div className="-mt-1">
              {unpaidLoading && !unpaidMonths.length ? (
                <div className="h-6 w-44 rounded-lg bg-nav-hover/50 animate-pulse" />
              ) : unpaidMonths.length === 0 ? (
                <p className="text-xs text-muted">No unpaid months</p>
              ) : (
                <div className="space-y-1.5">
                  <p className="text-xs text-muted">
                    Owes <span className="font-semibold text-secondary">{unpaidMonths.length}</span> month{unpaidMonths.length !== 1 ? 's' : ''}
                    {' · '}
                    <span className="font-semibold text-secondary">NPR {unpaidTotal.toLocaleString()}</span> total
                    <span className="text-subtle"> · tap to select one or more</span>
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {unpaidMonths.map((m, i) => {
                      const isOldest   = i === 0;
                      const isSelected = selectedMonths.includes(m.month);
                      // Oldest emphasized in the red family; others amber. Selected = filled tint.
                      const fam = isOldest
                        ? { on: 'bg-red-500/15 text-red-400 border-red-500/40',     off: 'text-red-400/80 border-red-500/25 hover:bg-red-500/10' }
                        : { on: 'bg-amber-500/15 text-amber-400 border-amber-500/40', off: 'text-amber-400/80 border-amber-500/25 hover:bg-amber-500/10' };
                      return (
                        <button
                          key={m.month}
                          type="button"
                          aria-pressed={isSelected}
                          onClick={() => toggleMonth(m.month)}
                          title={`${formatMonth(m.month)} · ${adHint(m.month)} · NPR ${m.amount.toLocaleString()}`}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border transition-colors ${isSelected ? fam.on : fam.off}`}
                        >
                          {isSelected && <MdCheck className="text-xs" />}
                          {formatMonth(m.month)}<span className="opacity-60"> · {adHint(m.month)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Custom charges — one-off fees. Multi-selectable toggles, exactly like month chips;
              each selected charge is paid by its own _id and adds to the total. */}
          {selectedStudent && customUnpaid.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs text-muted">Custom charges<span className="text-subtle"> · tap to select one or more</span></p>
              <div className="flex flex-wrap gap-1.5">
                {customUnpaid.map(c => {
                  const on = isChargeSelected(c);
                  return (
                    <button
                      key={c._id}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleCharge(c)}
                      title={`${c.description || 'Custom charge'} · NPR ${Number(c.totalAmount || c.amount || 0).toLocaleString()}`}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                        on
                          ? 'bg-nav-hover text-secondary border-secondary'
                          : 'border-divider bg-nav-hover text-muted hover:border-secondary'
                      }`}
                    >
                      {on && <MdCheck className="text-xs" />}
                      <span className="truncate max-w-[10rem]">{c.description || 'Custom charge'}</span>
                      <span className="opacity-70">· NPR {Number(c.totalAmount || c.amount || 0).toLocaleString()}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Selection summary — reflects months AND charges (e.g. "Paying 2 months + 1 charge") */}
          {(chipMode || chargeCount > 0) && (
            <div>
              <p className="text-sm text-secondary">
                Paying{' '}
                {monthCount > 0 && (
                  <span className="font-semibold">{monthCount} month{monthCount !== 1 ? 's' : ''}</span>
                )}
                {monthCount > 0 && chargeCount > 0 && ' + '}
                {chargeCount > 0 && (
                  <span className="font-semibold">{chargeCount} charge{chargeCount !== 1 ? 's' : ''}</span>
                )}
              </p>
              {chipMode && (
                <>
                  <p className="text-xs text-muted mt-0.5">
                    {monthsToPay.map((mo, i) => (
                      <span key={mo}>
                        {i > 0 && ', '}
                        {formatMonth(mo)}<span className="opacity-70"> · {adHint(mo)}</span>
                      </span>
                    ))}
                  </p>
                  <button
                    type="button"
                    onClick={() => setSelectedMonths([])}
                    className="text-xs text-muted hover:text-secondary transition-colors mt-1"
                  >
                    Clear months
                  </button>
                </>
              )}
            </div>
          )}

          {/* Month — single-month picker; used only for a pure monthly payment (no charges,
              no month chips). When charges are selected, months come from the chips above. */}
          {chargeCount === 0 && !chipMode && (
            <div>
              <label className={LABEL_CLS}>Month *</label>
              <DualDatePicker monthOnly value={month} onChange={(v) => { setMonth(v); setMonthTouched(true); }} />
            </div>
          )}

          {/* Method toggle */}
          <div>
            <label className={LABEL_CLS}>Payment Method *</label>
            <div className="grid grid-cols-3 gap-2">
              {/* Only the club's enabled methods (from Settings → Payment Methods); "Other" is
                  always available as an admin fallback. */}
              {[
                ['cash',    'Cash'],
                ['bank',    'Bank Transfer'],
                ['esewa',   'eSewa'],
                ['khalti',  'Khalti'],
                ['fonepay', 'Fonepay'],
                ['other',   'Other'],
              ].filter(([val]) => isMethodEnabled(val, acceptedMethods)).map(([val, lbl]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setMethod(val)}
                  className={`py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${
                    method === val
                      ? 'bg-secondary text-card border-secondary'
                      : 'bg-card border-divider text-muted hover:bg-nav-hover hover:border-secondary'
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          {/* Amount summary — auto from the fee structure + each fee doc's stamped late fee.
              A read-only breakdown; "Adjust amounts" reveals the editable inputs for edge cases. */}
          {(monthCount + chargeCount) > 0 && (
            <div className="space-y-3">
              <div className="bg-nav-hover rounded-xl px-4 py-3 space-y-2">
                {monthsToPay.map(mo => {
                  // Headline = remaining balance owed (never the full charge). For a month that
                  // already has part-payments, show what's been paid vs the charge underneath.
                  const owed = monthOwedOf(mo), paid = monthPaidOf(mo), charge = monthChargeOf(mo);
                  return (
                    <div key={mo} className="space-y-0.5">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm text-secondary truncate">{formatMonth(mo)}<span className="text-muted"> · {adHint(mo)}</span></span>
                        <span className="text-sm font-medium text-secondary whitespace-nowrap">NPR {owed.toLocaleString()}</span>
                      </div>
                      {paid > 0 && (
                        <div className="text-xs text-muted">NPR {paid.toLocaleString()} of NPR {charge.toLocaleString()} already paid</div>
                      )}
                    </div>
                  );
                })}
                {selectedCharges.map(c => (
                  <div key={c._id} className="flex items-center justify-between gap-3">
                    <span className="text-sm text-secondary truncate">{c.description || 'Custom charge'}</span>
                    <span className="text-sm font-medium text-secondary whitespace-nowrap">NPR {Number(c.totalAmount || c.amount || 0).toLocaleString()}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2 border-t border-divider">
                  <span className="text-sm font-medium text-muted">Total Amount</span>
                  <span className="text-lg font-bold text-secondary">NPR {totalAmount.toLocaleString()}</span>
                </div>
              </div>

              {/* Amount received — editable for a SINGLE existing fee (partial payments).
                  Defaults to the full outstanding, so paying in full is one tap, unchanged. */}
              {partialCapable && (
                <div className="space-y-1.5">
                  <label className={LABEL_CLS}>Amount received (NPR) *</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={amountInput}
                    onChange={e => { setAmountInput(e.target.value.replace(/[^\d.]/g, '')); setAmountTouched(true); }}
                    className={`${INPUT_CLS} min-h-[44px]`}
                    placeholder={String(outstanding)}
                  />
                  <p className="text-xs text-muted">Outstanding: <span className="font-semibold text-secondary">NPR {outstanding.toLocaleString()}</span></p>
                  {amountOver ? (
                    <div className="rounded-xl border border-divider bg-nav-hover px-3 py-2.5">
                      <p className="text-sm font-medium text-secondary">
                        NPR {outstanding.toLocaleString()} settles this. The extra NPR {(parsedAmount - outstanding).toLocaleString()} will be offered toward next month — you'll confirm before it's applied.
                      </p>
                    </div>
                  ) : (isPartialSubmit && parsedAmount > 0) ? (
                    <div className="rounded-xl border border-divider bg-nav-hover px-3 py-2.5">
                      <p className="text-sm font-medium text-secondary">Recording NPR {parsedAmount.toLocaleString()}</p>
                      <p className="text-xs text-muted mt-0.5">NPR {remainingAfter.toLocaleString()} will remain outstanding</p>
                    </div>
                  ) : null}
                </div>
              )}

              {/* Multiple fees selected → full payment each (partial is single-fee only). */}
              {!partialCapable && (monthCount + chargeCount) > 1 && (
                <p className="text-xs text-muted">Paying {monthCount + chargeCount} fees in full. To pay part of one, select just that fee.</p>
              )}

              {/* Adjust amounts — collapsed on every open. Base fee comes from the fee doc /
                  structure and the late fee from what the cron stamped (both read-only in the
                  summary above); only the discount is adjustable here. Hidden while recording a
                  partial amount (the discount doesn't apply to a part-payment). Conditional
                  render (not display:none) keeps the value in React state. */}
              {monthCount > 0 && !(partialCapable && parsedAmount !== outstanding) && (
                <div>
                  <button
                    type="button"
                    onClick={() => setAdjustOpen(o => !o)}
                    className="min-h-[44px] inline-flex items-center gap-1.5 text-xs font-medium text-muted hover:text-secondary transition-colors"
                  >
                    <MdTune className="text-sm" />
                    {adjustOpen ? 'Hide discount' : 'Adjust amounts'}
                  </button>

                  {adjustOpen && (
                    <div className="mt-1.5">
                      <div>
                        <label className={LABEL_CLS}>Discount (NPR)</label>
                        <input type="number" min="0" value={multi ? '0' : discount} disabled={multi}
                          onChange={e => { setDiscount(e.target.value); setManualDiscount(true); }}
                          className={`${INPUT_CLS} min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed`} placeholder="0" />
                      </div>
                      {multi && (
                        <p className="text-[11px] text-muted mt-1.5">Discount applies to single-month payments; multi-month keeps each month’s pre-assigned discount.</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Paid date */}
          <div>
            <label className={LABEL_CLS}>Date Received *</label>
            <input type="date" value={paidDate} onChange={e => setPaidDate(e.target.value)} className={INPUT_CLS} />
          </div>

          {/* Bank / digital payment reference fields */}
          {['bank', 'esewa', 'khalti', 'fonepay'].includes(method) && (
            <>
              {method === 'bank' && (
                <div>
                  <label className={LABEL_CLS}>Bank Name</label>
                  <input value={bankName} onChange={e => setBankName(e.target.value)}
                    placeholder="e.g. Nepal Investment Bank" className={INPUT_CLS} />
                </div>
              )}
              <div>
                <label className={LABEL_CLS}>
                  {method === 'bank' ? 'Transaction Reference / Voucher No.' : 'Transaction Reference'}
                </label>
                <input value={txRef} onChange={e => setTxRef(e.target.value)}
                  placeholder="e.g. TXN-12345" className={INPUT_CLS} />
              </div>
            </>
          )}

          {/* Notes */}
          <div>
            <label className={LABEL_CLS}>Notes (optional)</label>
            <input value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Any additional notes…" className={INPUT_CLS} />
          </div>

          {/* Payment proof — image/PDF; one file covers all selected months. Required for non-cash. */}
          <div>
            <label className={LABEL_CLS}>{proofRequired ? 'Payment proof *' : 'Payment proof (optional)'}</label>
            {proofFile ? (
              <div className="flex items-center gap-3 border border-divider rounded-xl px-3 py-2.5 bg-nav-hover/40">
                {proofPreview ? (
                  <img src={proofPreview} alt="Payment proof preview" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-nav-hover flex items-center justify-center flex-shrink-0">
                    <MdReceipt className="text-muted text-xl" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-secondary truncate">{proofFile.name}</p>
                  <p className="text-xs text-muted">{(proofFile.size / 1024).toFixed(0)} KB</p>
                </div>
                <button type="button" onClick={clearProof} title="Remove proof"
                  className="text-muted hover:text-secondary transition-colors flex-shrink-0">
                  <MdClose />
                </button>
              </div>
            ) : (
              <label className="flex items-center gap-2 border border-dashed border-divider rounded-xl px-3 py-2.5 cursor-pointer hover:bg-nav-hover/40 transition-colors text-sm text-muted">
                <MdUpload className="text-lg flex-shrink-0" />
                <span>Upload receipt / voucher — image or PDF (max 5 MB)</span>
                <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={onProofChange} />
              </label>
            )}
            {proofMissing && (
              <p className="text-[11px] text-muted mt-1">Proof required for bank/digital payments</p>
            )}
          </div>

        </div>

        {/* Pinned footer — stays visible while the body scrolls, so Submit is always reachable. */}
        <div className="flex justify-end gap-2 px-6 py-3 border-t border-divider bg-card rounded-b-2xl flex-shrink-0">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || submitting}>
            {submitting
              ? (multi ? `Recording ${monthCount} months…` : 'Recording…')
              : `Record NPR ${effectiveAmount.toLocaleString()}`}
          </Button>
        </div>
      </div>
      {confirmModal}
    </div>
  );
}
